import type { Model } from "../types";

/** Seed catalog — prices researched from official docs where cited. Never invent verified prices. */
export const models: Model[] = [
  // ─── OpenAI ───────────────────────────────────────────────
  {
    id: "openai-gpt-image-2",
    name: "GPT Image 2",
    providerId: "openai",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Text in $2.50/1M · Image in $4/1M · Image out $15/1M tokens",
      tiers: [
        { label: "Text input", amountUsd: 2.5, unit: "per_1m_tokens" },
        { label: "Image input", amountUsd: 4.0, unit: "per_1m_tokens" },
        { label: "Image output", amountUsd: 15.0, unit: "per_1m_tokens" },
      ],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing", "transparent-bg"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "sota",
    docsUrl: "https://developers.openai.com/api/docs/pricing",
    lastVerified: "2026-09-19",
    notes: "Per-image cost varies by size/quality; use OpenAI image cost calculator.",
  },
  {
    id: "openai-gpt-image-2.5-sunburst",
    name: "GPT Image 2.5 Sunburst",
    providerId: "openai",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Text in $5/1M · Image in $8/1M · Image out $30/1M tokens",
      tiers: [
        { label: "Text input", amountUsd: 5.0, unit: "per_1m_tokens" },
        { label: "Image input", amountUsd: 8.0, unit: "per_1m_tokens" },
        { label: "Image output", amountUsd: 30.0, unit: "per_1m_tokens" },
      ],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "sota",
    docsUrl: "https://developers.openai.com/api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "openai-gpt-image-2.5-flare",
    name: "GPT Image 2.5 Flare",
    providerId: "openai",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Text in $5/1M · Image in $8/1M · Image out $30/1M tokens",
      tiers: [
        { label: "Text input", amountUsd: 5.0, unit: "per_1m_tokens" },
        { label: "Image input", amountUsd: 8.0, unit: "per_1m_tokens" },
        { label: "Image output", amountUsd: 30.0, unit: "per_1m_tokens" },
      ],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "sota",
    docsUrl: "https://developers.openai.com/api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "openai-gpt-image-1.5",
    name: "GPT Image 1.5",
    providerId: "openai",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Low 1024² ~$0.009 · Med ~$0.034 · High ~$0.133 per image",
      tiers: [
        { label: "Low 1024x1024", amountUsd: 0.009, unit: "per_image" },
        { label: "Medium 1024x1024", amountUsd: 0.034, unit: "per_image" },
        { label: "High 1024x1024", amountUsd: 0.133, unit: "per_image" },
      ],
      normalizedUsd: 0.034,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://developers.openai.com/api/docs/models/gpt-image-1.5",
    lastVerified: "2026-09-19",
  },
  {
    id: "openai-gpt-image-1",
    name: "GPT Image 1",
    providerId: "openai",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Low 1024² ~$0.011 · Med ~$0.042 · High ~$0.167 per image",
      tiers: [
        { label: "Low 1024x1024", amountUsd: 0.011, unit: "per_image" },
        { label: "Medium 1024x1024", amountUsd: 0.042, unit: "per_image" },
        { label: "High 1024x1024", amountUsd: 0.167, unit: "per_image" },
      ],
      normalizedUsd: 0.042,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://developers.openai.com/api/docs/models/gpt-image-1",
    lastVerified: "2026-09-19",
  },
  {
    id: "openai-sora-2",
    name: "Sora 2",
    providerId: "openai",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "720p $0.1/sec (OpenAI pricing)",
      tiers: [
        { label: "720p", amountUsd: 0.1, unit: "per_second" },
      ],
      rawPricing: [
        { label: "720p", amountUsd: 0.1, unit: "per_second" },
      ],
      normalizedUsd: 0.1,
      normalizedUnit: "per_second",
      assumptions: "OpenAI listed 720p standard (non-batch) rate",
      confidence: "verified",
    },
    limits: { maxResolution: "1280x720" },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "good",
    docsUrl: "https://developers.openai.com/api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "openai-sora-2-pro",
    name: "Sora 2 Pro",
    providerId: "openai",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "720p $0.3/sec (OpenAI pricing)",
      tiers: [
        { label: "720p", amountUsd: 0.3, unit: "per_second" },
      ],
      rawPricing: [
        { label: "720p", amountUsd: 0.3, unit: "per_second" },
      ],
      normalizedUsd: 0.3,
      normalizedUnit: "per_second",
      assumptions: "OpenAI listed 720p standard (non-batch) rate",
      confidence: "verified",
    },
    limits: { maxResolution: "1920x1080" },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "sota",
    docsUrl: "https://developers.openai.com/api/docs/pricing",
    lastVerified: "2026-09-19",
  },

  // ─── Google Gemini ────────────────────────────────────────
  {
    id: "google-gemini-3.1-flash-image",
    name: "Gemini 3.1 Flash Image",
    providerId: "google-gemini",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.067 per 1K image (token-based: $60/1M image out tokens)",
      tiers: [
        { label: "0.5K image", amountUsd: 0.045, unit: "per_image" },
        { label: "1K image", amountUsd: 0.067, unit: "per_image" },
        { label: "2K image", amountUsd: 0.101, unit: "per_image" },
        { label: "4K image", amountUsd: 0.151, unit: "per_image" },
      ],
      normalizedUsd: 0.067,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "usable",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
    aliases: ["Nano Banana 2"],
  },
  {
    id: "google-gemini-3.1-flash-lite-image",
    name: "Gemini 3.1 Flash Lite Image",
    providerId: "google-gemini",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.0336 per 1K image",
      tiers: [{ label: "1K image", amountUsd: 0.0336, unit: "per_image" }],
      normalizedUsd: 0.0336,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "usable",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "google-gemini-3-pro-image",
    name: "Gemini 3 Pro Image",
    providerId: "google-gemini",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.134 per 1K/2K · $0.24 per 4K image",
      tiers: [
        { label: "1K/2K image", amountUsd: 0.134, unit: "per_image" },
        { label: "4K image", amountUsd: 0.24, unit: "per_image" },
      ],
      normalizedUsd: 0.134,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
    aliases: ["Nano Banana Pro"],
  },
  {
    id: "google-gemini-2.5-flash-image",
    name: "Gemini 2.5 Flash Image",
    providerId: "google-gemini",
    modality: "image",
    status: "deprecated",
    pricing: {
      raw: "$0.039 per image (up to 1024²)",
      tiers: [{ label: "≤1024x1024", amountUsd: 0.039, unit: "per_image" }],
      normalizedUsd: 0.039,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "usable",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
    notes: "Deprecated; shutdown Oct 2, 2026. Migrate to Gemini 3.1 Flash Image.",
    aliases: ["Nano Banana"],
  },
  {
    id: "google-imagen-4-fast",
    name: "Imagen 4 Fast",
    providerId: "google-gemini",
    modality: "image",
    status: "deprecated",
    pricing: {
      raw: "$0.02 per image",
      tiers: [{ label: "per image", amountUsd: 0.02, unit: "per_image" }],
      normalizedUsd: 0.02,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://ai.google.dev/gemini-api/docs/models/imagen",
    lastVerified: "2026-09-19",
    notes: "Deprecated; shutdown Aug 17, 2026.",
  },
  {
    id: "google-imagen-4",
    name: "Imagen 4",
    providerId: "google-gemini",
    modality: "image",
    status: "deprecated",
    pricing: {
      raw: "$0.04 per image",
      tiers: [{ label: "per image", amountUsd: 0.04, unit: "per_image" }],
      normalizedUsd: 0.04,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://ai.google.dev/gemini-api/docs/models/imagen",
    lastVerified: "2026-09-19",
    notes: "Deprecated; shutdown Aug 17, 2026.",
  },
  {
    id: "google-imagen-4-ultra",
    name: "Imagen 4 Ultra",
    providerId: "google-gemini",
    modality: "image",
    status: "deprecated",
    pricing: {
      raw: "$0.06 per image",
      tiers: [{ label: "per image", amountUsd: 0.06, unit: "per_image" }],
      normalizedUsd: 0.06,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "sota",
    docsUrl: "https://ai.google.dev/gemini-api/docs/models/imagen",
    lastVerified: "2026-09-19",
    notes: "Deprecated; shutdown Aug 17, 2026.",
  },
  {
    id: "google-veo-3.1",
    name: "Veo 3.1 Standard",
    providerId: "google-gemini",
    modality: "video",
    status: "preview",
    pricing: {
      raw: "Veo 3.1 $0.84 (per_second) via Krea",
      tiers: [
        { label: "Veo 3.1", amountUsd: 0.84, unit: "per_second" },
      ],
      rawPricing: [
        { label: "Veo 3.1", amountUsd: 0.84, unit: "per_second" },
      ],
      normalizedUsd: 0.84,
      normalizedUnit: "per_second",
      assumptions: "Krea API pricing page",
      confidence: "verified",
    },
    limits: { maxResolution: "4K" },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "sota",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "google-veo-3.1-fast",
    name: "Veo 3.1 Fast",
    providerId: "google-gemini",
    modality: "video",
    status: "preview",
    pricing: {
      raw: "$0.10/sec (720p) · $0.12 (1080p) · $0.30 (4K)",
      tiers: [
        { label: "720p", amountUsd: 0.1, unit: "per_second" },
        { label: "1080p", amountUsd: 0.12, unit: "per_second" },
        { label: "4K", amountUsd: 0.3, unit: "per_second" },
      ],
      normalizedUsd: 0.1,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "sota",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "google-veo-3.1-lite",
    name: "Veo 3.1 Lite",
    providerId: "google-gemini",
    modality: "video",
    status: "preview",
    pricing: {
      raw: "$0.05/sec (720p) · $0.08/sec (1080p)",
      tiers: [
        { label: "720p", amountUsd: 0.05, unit: "per_second" },
        { label: "1080p", amountUsd: 0.08, unit: "per_second" },
      ],
      normalizedUsd: 0.05,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "usable",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
    notes: "4K not supported.",
  },
  {
    id: "google-veo-3",
    name: "Veo 3 Standard",
    providerId: "google-gemini",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.40/sec with audio",
      tiers: [{ label: "with audio", amountUsd: 0.4, unit: "per_second" }],
      normalizedUsd: 0.4,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "good",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "google-veo-3-fast",
    name: "Veo 3 Fast",
    providerId: "google-gemini",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.10/sec (720p) · $0.12 (1080p) · $0.30 (4K)",
      tiers: [
        { label: "720p", amountUsd: 0.1, unit: "per_second" },
        { label: "1080p", amountUsd: 0.12, unit: "per_second" },
        { label: "4K", amountUsd: 0.3, unit: "per_second" },
      ],
      normalizedUsd: 0.1,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "usable",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "google-veo-2",
    name: "Veo 2",
    providerId: "google-gemini",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.35/sec",
      tiers: [{ label: "per second", amountUsd: 0.35, unit: "per_second" }],
      normalizedUsd: 0.35,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "google-gemini-omni-flash",
    name: "Gemini Omni Flash",
    providerId: "google-gemini",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~$0.10/sec at 720p (token-based video out)",
      tiers: [{ label: "720p effective", amountUsd: 0.1, unit: "per_second" }],
      normalizedUsd: 0.1,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "video-to-video", "editing"],
    strengths: { textToVideo: true, imageToVideo: true, edit: true },
    qualityRank: "usable",
    docsUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    lastVerified: "2026-09-19",
  },

  // ─── xAI ──────────────────────────────────────────────────
  {
    id: "xai-grok-imagine-image",
    name: "Grok Imagine Image",
    providerId: "xai",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "1K $0.002 (per_image) \u2014 xAI pricing",
      tiers: [
        { label: "1K", amountUsd: 0.002, unit: "per_image" },
      ],
      rawPricing: [
        { label: "1K", amountUsd: 0.002, unit: "per_image" },
      ],
      normalizedUsd: 0.002,
      normalizedUnit: "per_image",
      assumptions: "1K tier from xAI docs",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "usable",
    docsUrl: "https://docs.x.ai/developers/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "xai-grok-imagine-image-2",
    name: "Grok Imagine Image 2.0",
    providerId: "xai",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.04 per image",
      tiers: [{ label: "per image", amountUsd: 0.04, unit: "per_image" }],
      normalizedUsd: 0.04,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "usable",
    docsUrl: "https://docs.x.ai/developers/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "xai-grok-imagine-image-quality",
    name: "Grok Imagine Image Quality",
    providerId: "xai",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "1K $0.01 (per_image) \u2014 xAI pricing",
      tiers: [
        { label: "1K", amountUsd: 0.01, unit: "per_image" },
      ],
      rawPricing: [
        { label: "1K", amountUsd: 0.01, unit: "per_image" },
      ],
      normalizedUsd: 0.01,
      normalizedUnit: "per_image",
      assumptions: "1K tier from xAI docs",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://docs.x.ai/developers/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "xai-grok-imagine-video",
    name: "Grok Imagine Video",
    providerId: "xai",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "720p $0.07 (per_second) \u2014 xAI pricing",
      tiers: [
        { label: "720p", amountUsd: 0.07, unit: "per_second" },
      ],
      rawPricing: [
        { label: "720p", amountUsd: 0.07, unit: "per_second" },
      ],
      normalizedUsd: 0.07,
      normalizedUnit: "per_second",
      assumptions: "720p tier from xAI docs",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.x.ai/developers/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "xai-grok-imagine-video-1.5",
    name: "Grok Imagine Video 1.5",
    providerId: "xai",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "720p $0.14 (per_second) \u2014 xAI pricing",
      tiers: [
        { label: "720p", amountUsd: 0.14, unit: "per_second" },
      ],
      rawPricing: [
        { label: "720p", amountUsd: 0.14, unit: "per_second" },
      ],
      normalizedUsd: 0.14,
      normalizedUnit: "per_second",
      assumptions: "720p tier from xAI docs",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.x.ai/developers/pricing",
    lastVerified: "2026-09-19",
  },

  // ─── Black Forest Labs ────────────────────────────────────
  {
    id: "bfl-flux-2-pro",
    name: "FLUX.2 Pro",
    providerId: "bfl",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "from $0.03 / MP",
      tiers: [{ label: "per megapixel", amountUsd: 0.03, unit: "per_megapixel" }],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://docs.bfl.ai/flux_2/flux2_overview",
    lastVerified: "2026-09-19",
    notes: "Normalized assumes ~1 MP output.",
  },
  {
    id: "bfl-flux-2-flex",
    name: "FLUX.2 Flex",
    providerId: "bfl",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.06 / MP",
      tiers: [{ label: "per megapixel", amountUsd: 0.06, unit: "per_megapixel" }],
      normalizedUsd: 0.06,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://docs.bfl.ai/flux_2/flux2_overview",
    lastVerified: "2026-09-19",
  },
  {
    id: "bfl-flux-2-max",
    name: "FLUX.2 Max",
    providerId: "bfl",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "from $0.07 / MP",
      tiers: [{ label: "per megapixel", amountUsd: 0.07, unit: "per_megapixel" }],
      normalizedUsd: 0.07,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://docs.bfl.ai/flux_2/flux2_overview",
    lastVerified: "2026-09-19",
  },
  {
    id: "bfl-flux-2-klein-4b",
    name: "FLUX.2 Klein 4B",
    providerId: "bfl",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.014 + $0.001/MP",
      tiers: [{ label: "base + per MP", amountUsd: 0.014, unit: "per_image" }],
      normalizedUsd: 0.015,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "usable",
    docsUrl: "https://docs.bfl.ai/flux_2/flux2_overview",
    lastVerified: "2026-09-19",
  },
  {
    id: "bfl-flux-2-klein-9b",
    name: "FLUX.2 Klein 9B",
    providerId: "bfl",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.015 + $0.002/MP",
      tiers: [{ label: "base + per MP", amountUsd: 0.015, unit: "per_image" }],
      normalizedUsd: 0.017,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "usable",
    docsUrl: "https://docs.bfl.ai/flux_2/flux2_overview",
    lastVerified: "2026-09-19",
  },

  // ─── Stability ────────────────────────────────────────────
  {
    id: "stability-ultra",
    name: "Stable Image Ultra",
    providerId: "stability",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "8 credits ($0.08) per image",
      tiers: [{ label: "8 credits", amountUsd: 0.08, unit: "per_image" }],
      normalizedUsd: 0.08,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://platform.stability.ai/pricing",
    lastVerified: "2026-09-19",
    notes: "1 credit = $0.01.",
  },
  {
    id: "stability-sd35-large",
    name: "Stable Diffusion 3.5 Large",
    providerId: "stability",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "6.5 credits ($0.065) per image",
      tiers: [{ label: "6.5 credits", amountUsd: 0.065, unit: "per_image" }],
      normalizedUsd: 0.065,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "good",
    docsUrl: "https://platform.stability.ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "stability-sd35-large-turbo",
    name: "Stable Diffusion 3.5 Large Turbo",
    providerId: "stability",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "4 credits ($0.04) per image",
      tiers: [{ label: "4 credits", amountUsd: 0.04, unit: "per_image" }],
      normalizedUsd: 0.04,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://platform.stability.ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "stability-sd35-medium",
    name: "Stable Diffusion 3.5 Medium",
    providerId: "stability",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "3.5 credits ($0.035) per image",
      tiers: [{ label: "3.5 credits", amountUsd: 0.035, unit: "per_image" }],
      normalizedUsd: 0.035,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://platform.stability.ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "stability-sd35-flash",
    name: "Stable Diffusion 3.5 Flash",
    providerId: "stability",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "2.5 credits ($0.025) per image",
      tiers: [{ label: "2.5 credits", amountUsd: 0.025, unit: "per_image" }],
      normalizedUsd: 0.025,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://platform.stability.ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "stability-core",
    name: "Stable Image Core",
    providerId: "stability",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "3 credits ($0.03) per image",
      tiers: [{ label: "3 credits", amountUsd: 0.03, unit: "per_image" }],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://platform.stability.ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "stability-sdxl",
    name: "SDXL 1.0",
    providerId: "stability",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "From 0.9 credits (~$0.009) per image",
      tiers: [{ label: "from 0.9 credits", amountUsd: 0.009, unit: "per_image" }],
      normalizedUsd: 0.009,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "inpainting"],
    strengths: { textToImage: true, imageToImage: true, inpaint: true },
    qualityRank: "usable",
    docsUrl: "https://platform.stability.ai/pricing",
    lastVerified: "2026-09-19",
    notes: "Legacy model.",
  },

  // ─── Ideogram ─────────────────────────────────────────────
  {
    id: "ideogram-4-turbo",
    name: "Ideogram 4.0 Turbo",
    providerId: "ideogram",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.03 per image",
      tiers: [{ label: "per image", amountUsd: 0.03, unit: "per_image" }],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "character-reference"],
    strengths: { textToImage: true, imageToImage: true, characterReference: true },
    qualityRank: "usable",
    docsUrl: "https://ideogram.ai/api-pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "ideogram-4-default",
    name: "Ideogram 4.0 Default",
    providerId: "ideogram",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.06 per image",
      tiers: [{ label: "per image", amountUsd: 0.06, unit: "per_image" }],
      normalizedUsd: 0.06,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "character-reference"],
    strengths: { textToImage: true, imageToImage: true, characterReference: true },
    qualityRank: "good",
    docsUrl: "https://ideogram.ai/api-pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "ideogram-4-quality",
    name: "Ideogram 4.0 Quality",
    providerId: "ideogram",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.10 per image",
      tiers: [{ label: "per image", amountUsd: 0.1, unit: "per_image" }],
      normalizedUsd: 0.1,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "character-reference"],
    strengths: { textToImage: true, imageToImage: true, characterReference: true },
    qualityRank: "good",
    docsUrl: "https://ideogram.ai/api-pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "ideogram-3-turbo",
    name: "Ideogram 3.0 Turbo",
    providerId: "ideogram",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.03 per image",
      tiers: [{ label: "per image", amountUsd: 0.03, unit: "per_image" }],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://ideogram.ai/api-pricing/",
    lastVerified: "2026-09-19",
  },

  // ─── Cloudflare Workers AI ────────────────────────────────
  {
    id: "cf-flux-1-schnell",
    name: "FLUX.1 Schnell (Workers AI)",
    providerId: "cloudflare",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.0000528 512 tile (CF Workers AI)",
      tiers: [
        { label: "512 tile", amountUsd: 0.0002112, unit: "per_image" },
      ],
      rawPricing: [
        { label: "512 tile", amountUsd: 0.0002112, unit: "per_image" },
      ],
      normalizedUsd: 0.0002112,
      normalizedUnit: "per_image",
      assumptions: "512 tile \u00d74 for ~1024\u00b2",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://developers.cloudflare.com/workers-ai/platform/pricing/",
    lastVerified: "2026-09-19",
    notes: "Normalized estimate for typical 1024² / ~4 steps. Billed via Neurons.",
  },
  {
    id: "cf-flux-2-dev",
    name: "FLUX.2 Dev (Workers AI)",
    providerId: "cloudflare",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.00021 in / $0.00041 out per 512² tile per step",
      tiers: [
        { label: "input tile/step", amountUsd: 0.00021, unit: "per_request" },
        { label: "output tile/step", amountUsd: 0.00041, unit: "per_request" },
      ],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "usable",
    docsUrl: "https://developers.cloudflare.com/workers-ai/platform/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "cf-flux-2-klein-4b",
    name: "FLUX.2 Klein 4B (Workers AI)",
    providerId: "cloudflare",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.015 first MP (CF Workers AI)",
      tiers: [
        { label: "first MP", amountUsd: 0.015, unit: "per_megapixel" },
      ],
      rawPricing: [
        { label: "first MP", amountUsd: 0.015, unit: "per_megapixel" },
      ],
      normalizedUsd: 0.015,
      normalizedUnit: "per_mp",
      assumptions: "First megapixel price from Cloudflare Workers AI",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "usable",
    docsUrl: "https://developers.cloudflare.com/workers-ai/platform/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "cf-flux-2-klein-9b",
    name: "FLUX.2 Klein 9B (Workers AI)",
    providerId: "cloudflare",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.015 first MP + $0.002 subsequent MP",
      tiers: [
        { label: "first MP", amountUsd: 0.015, unit: "per_megapixel" },
        { label: "subsequent MP", amountUsd: 0.002, unit: "per_megapixel" },
      ],
      normalizedUsd: 0.015,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "usable",
    docsUrl: "https://developers.cloudflare.com/workers-ai/platform/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "cf-leonardo-lucid-origin",
    name: "Leonardo Lucid Origin (Workers AI)",
    providerId: "cloudflare",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.006996 512 tile (CF Workers AI)",
      tiers: [
        { label: "512 tile", amountUsd: 0.027984, unit: "per_image" },
      ],
      rawPricing: [
        { label: "512 tile", amountUsd: 0.027984, unit: "per_image" },
      ],
      normalizedUsd: 0.027984,
      normalizedUnit: "per_image",
      assumptions: "512 tile \u00d74 for ~1024\u00b2",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://developers.cloudflare.com/workers-ai/models/lucid-origin/",
    lastVerified: "2026-09-19",
  },
  {
    id: "cf-leonardo-phoenix",
    name: "Leonardo Phoenix 1.0 (Workers AI)",
    providerId: "cloudflare",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.00583 / 512² tile + $0.00011 / step",
      tiers: [
        { label: "per 512 tile", amountUsd: 0.00583, unit: "per_request" },
        { label: "per step", amountUsd: 0.00011, unit: "per_request" },
      ],
      normalizedUsd: 0.025,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://developers.cloudflare.com/workers-ai/platform/pricing/",
    lastVerified: "2026-09-19",
  },

  // ─── Runway ───────────────────────────────────────────────
  {
    id: "runway-gen4-turbo",
    name: "Runway Gen-4 Turbo",
    providerId: "runway",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~5 credits/sec ≈ $0.05/sec ($0.01/credit)",
      tiers: [{ label: "per second", amountUsd: 0.05, unit: "per_second" }],
      normalizedUsd: 0.05,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["text-to-video", "image-to-video", "video-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "usable",
    docsUrl: "https://docs.dev.runwayml.com/guides/pricing/",
    lastVerified: "2026-09-19",
    notes: "Credit rates vary by plan; verify current credit costs in Runway docs.",
  },
  {
    id: "runway-gen4",
    name: "Runway Gen-4",
    providerId: "runway",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~10 credits/sec ≈ $0.10/sec",
      tiers: [{ label: "per second", amountUsd: 0.1, unit: "per_second" }],
      normalizedUsd: 0.1,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["text-to-video", "image-to-video", "video-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.dev.runwayml.com/guides/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "runway-gen4.5",
    name: "Runway Gen-4.5",
    providerId: "runway",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Gen-4.5 $0.126 (per_second) via Krea",
      tiers: [
        { label: "Gen-4.5", amountUsd: 0.126, unit: "per_second" },
      ],
      rawPricing: [
        { label: "Gen-4.5", amountUsd: 0.126, unit: "per_second" },
      ],
      normalizedUsd: 0.126,
      normalizedUnit: "per_second",
      assumptions: "Krea API pricing page",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "video-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.dev.runwayml.com/guides/pricing/",
    lastVerified: "2026-09-19",
  },

  // ─── fal.ai hosted models ─────────────────────────────────
  {
    id: "fal-flux-pro",
    name: "FLUX Pro (fal)",
    providerId: "fal",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.05 per_mp (fal model page)",
      tiers: [
        { label: "per_mp", amountUsd: 0.05, unit: "per_megapixel" },
      ],
      rawPricing: [
        { label: "per_mp", amountUsd: 0.05, unit: "per_megapixel" },
      ],
      normalizedUsd: 0.05,
      normalizedUnit: "per_mp",
      assumptions: "fal unit price; 1024\u00b2 \u2248 1 MP for image compare",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models/fal-ai/flux-pro",
    lastVerified: "2026-09-19",
    notes: "fal host pricing; confirm on model page.",
  },
  {
    id: "fal-flux-dev",
    name: "FLUX Dev (fal)",
    providerId: "fal",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.025 per_mp (fal model page)",
      tiers: [
        { label: "per_mp", amountUsd: 0.025, unit: "per_megapixel" },
      ],
      rawPricing: [
        { label: "per_mp", amountUsd: 0.025, unit: "per_megapixel" },
      ],
      normalizedUsd: 0.025,
      normalizedUnit: "per_mp",
      assumptions: "fal unit price; 1024\u00b2 \u2248 1 MP for image compare",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "lora"],
    strengths: { textToImage: true, lora: true },
    qualityRank: "usable",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-flux-schnell",
    name: "FLUX Schnell (fal)",
    providerId: "fal",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.003 per_mp (fal model page)",
      tiers: [
        { label: "per_mp", amountUsd: 0.003, unit: "per_megapixel" },
      ],
      rawPricing: [
        { label: "per_mp", amountUsd: 0.003, unit: "per_megapixel" },
      ],
      normalizedUsd: 0.003,
      normalizedUnit: "per_mp",
      assumptions: "fal unit price; 1024\u00b2 \u2248 1 MP for image compare",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-kling-2.6-pro",
    name: "Kling 2.6 Pro (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~$0.07 / sec (host-listed)",
      tiers: [{ label: "per second", amountUsd: 0.07, unit: "per_second" }],
      normalizedUsd: 0.07,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-kling-2.6-audio",
    name: "Kling 2.6 + Audio (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~$0.14 / sec (host-listed)",
      tiers: [{ label: "per second", amountUsd: 0.14, unit: "per_second" }],
      normalizedUsd: 0.14,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-wan-2.6",
    name: "Wan 2.6 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~$0.05 / sec (host-listed)",
      tiers: [{ label: "per second", amountUsd: 0.05, unit: "per_second" }],
      normalizedUsd: 0.05,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-luma-ray-2",
    name: "Luma Ray 2 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.50 / 5s at 540p · 2× 720p · 4× 1080p",
      tiers: [
        { label: "540p / 5s", amountUsd: 0.5, unit: "per_request" },
        { label: "effective 540p / sec", amountUsd: 0.1, unit: "per_second" },
      ],
      normalizedUsd: 0.1,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models/fal-ai/luma-dream-machine/ray-2",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-luma-ray-3.2",
    name: "Luma Ray 3.2 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.50 / 5s 540p · $1 720p · $2 1080p",
      tiers: [
        { label: "540p / 5s", amountUsd: 0.5, unit: "per_request" },
        { label: "720p / 5s", amountUsd: 1.0, unit: "per_request" },
        { label: "1080p / 5s", amountUsd: 2.0, unit: "per_request" },
      ],
      normalizedUsd: 0.1,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models/luma/agent/ray/v3.2/text-to-video",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-veo-3.1",
    name: "Veo 3.1 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.4 per_second (fal model page)",
      tiers: [
        { label: "per_second", amountUsd: 0.4, unit: "per_second" },
      ],
      rawPricing: [
        { label: "per_second", amountUsd: 0.4, unit: "per_second" },
      ],
      normalizedUsd: 0.4,
      normalizedUnit: "per_second",
      assumptions: "fal unit price; prefer listed resolution when present",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "sota",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-ideogram-v3",
    name: "Ideogram V3 (fal)",
    providerId: "fal",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.03 / image (host-listed)",
      tiers: [{ label: "per image", amountUsd: 0.03, unit: "per_image" }],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-recraft-v3",
    name: "Recraft V3 (fal)",
    providerId: "fal",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.04 / image (host-listed)",
      tiers: [{ label: "per image", amountUsd: 0.04, unit: "per_image" }],
      normalizedUsd: 0.04,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "sota",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-minimax-hailuo",
    name: "MiniMax Hailuo (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.045 per_second (fal model page)",
      tiers: [
        { label: "per_second", amountUsd: 0.045, unit: "per_second" },
      ],
      rawPricing: [
        { label: "per_second", amountUsd: 0.045, unit: "per_second" },
      ],
      normalizedUsd: 0.045,
      normalizedUnit: "per_second",
      assumptions: "fal unit price; prefer listed resolution when present",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },

  // ─── Replicate ────────────────────────────────────────────
  {
    id: "replicate-flux-schnell",
    name: "FLUX Schnell (Replicate)",
    providerId: "replicate",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.003/image (Replicate)",
      tiers: [
        { label: "output image", amountUsd: 0.003, unit: "per_image" },
      ],
      rawPricing: [
        { label: "output image", amountUsd: 0.003, unit: "per_image" },
      ],
      normalizedUsd: 0.003,
      normalizedUnit: "per_image",
      assumptions: "replicate.com/pricing",
      confidence: "verified",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://replicate.com/black-forest-labs/flux-schnell",
    lastVerified: "2026-09-19",
  },
  {
    id: "replicate-flux-dev",
    name: "FLUX Dev (Replicate)",
    providerId: "replicate",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.03 / image (host-listed)",
      tiers: [{ label: "per image", amountUsd: 0.03, unit: "per_image" }],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image", "lora"],
    strengths: { textToImage: true, lora: true },
    qualityRank: "usable",
    docsUrl: "https://replicate.com/black-forest-labs/flux-dev",
    lastVerified: "2026-09-19",
  },
  {
    id: "replicate-wan-2.1-480p",
    name: "Wan 2.1 I2V 480p (Replicate)",
    providerId: "replicate",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~$0.09 / sec at 480p (host-listed)",
      tiers: [{ label: "480p per second", amountUsd: 0.09, unit: "per_second" }],
      normalizedUsd: 0.09,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["image-to-video", "text-to-video"],
    strengths: { imageToVideo: true, textToVideo: true },
    qualityRank: "good",
    docsUrl: "https://replicate.com/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "replicate-wan-2.1-720p",
    name: "Wan 2.1 I2V 720p (Replicate)",
    providerId: "replicate",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~$0.25 / sec at 720p (host-listed)",
      tiers: [{ label: "720p per second", amountUsd: 0.25, unit: "per_second" }],
      normalizedUsd: 0.25,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["image-to-video", "text-to-video"],
    strengths: { imageToVideo: true, textToVideo: true },
    qualityRank: "good",
    docsUrl: "https://replicate.com/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "replicate-sdxl",
    name: "SDXL (Replicate)",
    providerId: "replicate",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.005 / image (host-listed)",
      tiers: [{ label: "per image", amountUsd: 0.005, unit: "per_image" }],
      normalizedUsd: 0.005,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image", "image-to-image", "inpainting"],
    strengths: { textToImage: true, imageToImage: true, inpaint: true },
    qualityRank: "usable",
    docsUrl: "https://replicate.com/stability-ai/sdxl",
    lastVerified: "2026-09-19",
  },

  // ─── Amazon Bedrock ───────────────────────────────────────
  {
    id: "bedrock-titan-image",
    name: "Amazon Titan Image Generator",
    providerId: "bedrock",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Region/quality dependent — verify AWS pricing page",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image", "image-to-image", "inpainting", "outpainting"],
    strengths: { textToImage: true, imageToImage: true, inpaint: true, outpaint: true },
    qualityRank: "usable",
    docsUrl: "https://aws.amazon.com/bedrock/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "bedrock-stable-image-core",
    name: "Stable Image Core (Bedrock)",
    providerId: "bedrock",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Verify AWS Bedrock Stability pricing (often ~$0.03–0.08/gen)",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://aws.amazon.com/bedrock/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "bedrock-stable-remove-bg",
    name: "Stable Image Remove Background (Bedrock)",
    providerId: "bedrock",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.07 per generation",
      tiers: [{ label: "per generation", amountUsd: 0.07, unit: "per_image" }],
      normalizedUsd: 0.07,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["editing", "transparent-bg"],
    strengths: { edit: true },
    qualityRank: "usable",
    docsUrl: "https://aws.amazon.com/bedrock/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "bedrock-stable-upscale-fast",
    name: "Stable Image Fast Upscale (Bedrock)",
    providerId: "bedrock",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.03 per generation",
      tiers: [{ label: "per generation", amountUsd: 0.03, unit: "per_image" }],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["upscaling"],
    strengths: { upscale: true },
    qualityRank: "usable",
    docsUrl: "https://aws.amazon.com/bedrock/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "bedrock-stable-creative-upscale",
    name: "Stable Image Creative Upscale (Bedrock)",
    providerId: "bedrock",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.60 per generation",
      tiers: [{ label: "per generation", amountUsd: 0.6, unit: "per_image" }],
      normalizedUsd: 0.6,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    capabilities: ["upscaling"],
    strengths: { upscale: true },
    qualityRank: "usable",
    docsUrl: "https://aws.amazon.com/bedrock/pricing/",
    lastVerified: "2026-09-19",
  },

  // ─── Azure ────────────────────────────────────────────────
  {
    id: "azure-gpt-image",
    name: "GPT Image (Azure OpenAI)",
    providerId: "azure",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Typically matches OpenAI direct; verify Azure price sheet",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://azure.microsoft.com/pricing/details/cognitive-services/openai-service/",
    lastVerified: "2026-09-19",
  },
  {
    id: "azure-dalle-3",
    name: "DALL·E 3 (Azure OpenAI)",
    providerId: "azure",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Standard ~$0.04 · HD ~$0.08 per image (legacy Azure rates; verify)",
      tiers: [
        { label: "Standard (est.)", amountUsd: 0.04, unit: "per_image" },
        { label: "HD (est.)", amountUsd: 0.08, unit: "per_image" },
      ],
      normalizedUsd: 0.04,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://azure.microsoft.com/pricing/details/cognitive-services/openai-service/",
    lastVerified: "2026-09-19",
  },

  // ─── Recraft ──────────────────────────────────────────────
  {
    id: "recraft-v3",
    name: "Recraft V3",
    providerId: "recraft",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.04 per image (direct / fal listed)",
      tiers: [{ label: "per image", amountUsd: 0.04, unit: "per_image" }],
      normalizedUsd: 0.04,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image", "style-reference"],
    strengths: { textToImage: true, styleReference: true },
    qualityRank: "sota",
    docsUrl: "https://www.recraft.ai/docs",
    lastVerified: "2026-09-19",
  },

  // ─── Together / Fireworks ─────────────────────────────────
  {
    id: "together-flux-schnell",
    name: "FLUX Schnell (Together)",
    providerId: "together",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Verify Together pricing page",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://www.together.ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "fireworks-flux",
    name: "FLUX (Fireworks)",
    providerId: "fireworks",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Verify Fireworks pricing page",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://fireworks.ai/pricing",
    lastVerified: "2026-09-19",
  },

  // ─── Luma direct ──────────────────────────────────────────
  {
    id: "luma-ray-2-direct",
    name: "Luma Ray 2 (direct)",
    providerId: "luma",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Credit/subscription based on Luma plans; API host pricing on fal",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.lumalabs.ai",
    lastVerified: "2026-09-19",
    notes: "See fal-hosted Ray 2 for transparent per-second API rates.",
  },

  // ─── Aggregators / regional hosts ─────────────────────────
  {
    id: "kie-kling",
    name: "Kling (Kie.ai host)",
    providerId: "kie",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Credit-based; verify Kie dashboard",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://kie.ai/docs",
    lastVerified: "2026-09-19",
  },
  {
    id: "atlascloud-multi",
    name: "AtlasCloud multi-model gateway",
    providerId: "atlascloud",
    modality: "both",
    status: "ga",
    pricing: {
      raw: "Varies by routed model; verify AtlasCloud pricing",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image", "text-to-video", "image-to-video"],
    strengths: { textToImage: true, textToVideo: true, imageToVideo: true },
    qualityRank: "usable",
    docsUrl: "https://www.atlascloud.ai/docs",
    lastVerified: "2026-09-19",
  },
  {
    id: "byteplus-seedance",
    name: "Seedance (BytePlus)",
    providerId: "byteplus",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Regional pricing; verify BytePlus ModelArk",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.byteplus.com",
    lastVerified: "2026-09-19",
  },
  {
    id: "alibaba-wan",
    name: "Wan / Tongyi Wanxiang (Alibaba)",
    providerId: "alibaba",
    modality: "both",
    status: "ga",
    pricing: {
      raw: "Regional CNY pricing on Model Studio; fal hosts often cheaper USD",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image", "text-to-video", "image-to-video"],
    strengths: { textToImage: true, textToVideo: true, imageToVideo: true },
    qualityRank: "usable",
    docsUrl: "https://www.alibabacloud.com/help/en/model-studio/",
    lastVerified: "2026-09-19",
  },
  {
    id: "minimax-hailuo-direct",
    name: "MiniMax Hailuo (direct)",
    providerId: "minimax",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Verify MiniMax platform pricing",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://www.minimax.io/platform",
    lastVerified: "2026-09-19",
  },
  {
    id: "vertex-imagen",
    name: "Imagen (Vertex AI)",
    providerId: "google-vertex",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Vertex list prices; often higher than Gemini API consumer rates",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image", "image-to-image", "upscaling"],
    strengths: { textToImage: true, imageToImage: true, upscale: true },
    qualityRank: "usable",
    docsUrl: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "vertex-veo",
    name: "Veo (Vertex AI)",
    providerId: "google-vertex",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Vertex enterprise rates; compare to Gemini API Veo pricing",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "good",
    docsUrl: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "leonardo-phoenix-direct",
    name: "Leonardo Phoenix (direct)",
    providerId: "leonardo",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Token/credit plans on Leonardo; Workers AI lists unit rates",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image", "image-to-image", "controlnet"],
    strengths: { textToImage: true, imageToImage: true, controlnet: true },
    qualityRank: "good",
    docsUrl: "https://docs.leonardo.ai",
    lastVerified: "2026-09-19",
  },

  // ─── Expansion batch (2026-09-19) ─────────────────────────
  {
    id: "deepinfra-flux-schnell",
    name: "FLUX Schnell (DeepInfra)",
    providerId: "deepinfra",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.0005–$0.003 / image (host-listed; verify DeepInfra pricing)",
      tiers: [{ label: "per image (approx)", amountUsd: 0.002, unit: "per_image" }],
      normalizedUsd: 0.002,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://deepinfra.com/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "deepinfra-sdxl",
    name: "SDXL (DeepInfra)",
    providerId: "deepinfra",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Verify DeepInfra SDXL unit price",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "usable",
    docsUrl: "https://deepinfra.com/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "novita-flux-dev",
    name: "FLUX Dev (Novita)",
    providerId: "novita",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.02–$0.04 / image (host-listed)",
      tiers: [{ label: "per image (approx)", amountUsd: 0.03, unit: "per_image" }],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image", "lora"],
    strengths: { textToImage: true, lora: true },
    qualityRank: "usable",
    docsUrl: "https://novita.ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "novita-wan-2.2",
    name: "Wan 2.2 (Novita)",
    providerId: "novita",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Host-listed per-second; verify Novita dashboard",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://novita.ai/docs",
    lastVerified: "2026-09-19",
  },
  {
    id: "pixverse-v5",
    name: "PixVerse V5",
    providerId: "pixverse",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Credit packs on PixVerse; fal/host USD often ~$0.05–0.15/sec",
      tiers: [{ label: "per second (approx via hosts)", amountUsd: 0.08, unit: "per_second" }],
      normalizedUsd: 0.08,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.platform.pixverse.ai",
    lastVerified: "2026-09-19",
  },
  {
    id: "pixverse-v4.5",
    name: "PixVerse V4.5",
    providerId: "pixverse",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Credit-based direct; verify host listings for USD",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.platform.pixverse.ai",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-pixverse-v5",
    name: "PixVerse V5 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "~$0.08 / sec (host-listed; confirm on fal model page)",
      tiers: [{ label: "per second", amountUsd: 0.08, unit: "per_second" }],
      normalizedUsd: 0.08,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-pika-2.2",
    name: "Pika 2.2 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.039 per_image (fal model page)",
      tiers: [
        { label: "per_image", amountUsd: 0.039, unit: "per_image" },
      ],
      rawPricing: [
        { label: "per_image", amountUsd: 0.039, unit: "per_image" },
      ],
      normalizedUsd: 0.039,
      normalizedUnit: "per_image",
      assumptions: "fal unit price per image",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "pika-2.1-direct",
    name: "Pika 2.1 (direct)",
    providerId: "pika",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Subscription/credit plans on Pika; not a flat public API tariff",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.pika.art",
    lastVerified: "2026-09-19",
  },
  {
    id: "getimg-essential-v2",
    name: "Essential v2 (Getimg)",
    providerId: "getimg",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Plan credits on Getimg; typical ~$0.002–$0.02/image depending on plan",
      tiers: [{ label: "per image (approx)", amountUsd: 0.01, unit: "per_image" }],
      normalizedUsd: 0.01,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image", "image-to-image"],
    strengths: { textToImage: true, imageToImage: true },
    qualityRank: "usable",
    docsUrl: "https://docs.getimg.ai",
    lastVerified: "2026-09-19",
  },
  {
    id: "getimg-flux",
    name: "FLUX (Getimg)",
    providerId: "getimg",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Verify Getimg Flux credit cost",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://docs.getimg.ai",
    lastVerified: "2026-09-19",
  },
  {
    id: "freepik-mystic",
    name: "Freepik Mystic",
    providerId: "freepik",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Freepik AI credit plans; verify API pricing docs",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "usable",
    docsUrl: "https://docs.freepik.com",
    lastVerified: "2026-09-19",
  },
  {
    id: "freepik-video",
    name: "Freepik Video",
    providerId: "freepik",
    modality: "video",
    status: "preview",
    pricing: {
      raw: "Credit-based; verify Freepik AI pricing",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://docs.freepik.com",
    lastVerified: "2026-09-19",
  },
  {
    id: "kling-2.1-official",
    name: "Kling 2.1 (official)",
    providerId: "kling",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Official credit packs; fal lists clearer USD (~$0.07/sec Pro)",
      tiers: [{ label: "unknown direct", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://app.klingai.com/global/dev/document-api",
    lastVerified: "2026-09-19",
  },
  {
    id: "kling-1.6-official",
    name: "Kling 1.6 (official)",
    providerId: "kling",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Credit-based official API",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://app.klingai.com/global/dev/document-api",
    lastVerified: "2026-09-19",
  },
  {
    id: "wavespeed-flux",
    name: "FLUX (WaveSpeed)",
    providerId: "wavespeed",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Verify WaveSpeed pricing page",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://wavespeed.ai/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-flux-kontext",
    name: "FLUX.1 Kontext (fal)",
    providerId: "fal",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.04 per_image (fal model page)",
      tiers: [
        { label: "per_image", amountUsd: 0.04, unit: "per_image" },
      ],
      rawPricing: [
        { label: "per_image", amountUsd: 0.04, unit: "per_image" },
      ],
      normalizedUsd: 0.04,
      normalizedUnit: "per_image",
      assumptions: "fal unit price per image",
      confidence: "verified",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "fal-hailuo-02",
    name: "MiniMax Hailuo 02 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.045 per_second (fal model page)",
      tiers: [
        { label: "per_second", amountUsd: 0.045, unit: "per_second" },
      ],
      rawPricing: [
        { label: "per_second", amountUsd: 0.045, unit: "per_second" },
      ],
      normalizedUsd: 0.045,
      normalizedUnit: "per_second",
      assumptions: "fal unit price; prefer listed resolution when present",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video"],
    strengths: { textToVideo: true, imageToVideo: true },
    qualityRank: "good",
    docsUrl: "https://fal.ai/models",
    lastVerified: "2026-09-19",
  },
  {
    id: "replicate-ideogram-v3",
    name: "Ideogram V3 (Replicate)",
    providerId: "replicate",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.03–$0.06 / image (host-listed)",
      tiers: [{ label: "per image (approx)", amountUsd: 0.04, unit: "per_image" }],
      normalizedUsd: 0.04,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://replicate.com/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "bfl-flux-1-kontext-pro",
    name: "FLUX.1 Kontext Pro (BFL)",
    providerId: "bfl",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Verify BFL Kontext Pro per-image / MP rates",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image", "image-to-image", "editing"],
    strengths: { textToImage: true, imageToImage: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://docs.bfl.ai",
    lastVerified: "2026-09-19",
  },
  {
    id: "runway-act-two",
    name: "Runway Act-Two",
    providerId: "runway",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Credit-based character performance; verify Runway API pricing guide",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["image-to-video", "video-to-video", "character-reference"],
    strengths: { imageToVideo: true, characterReference: true },
    qualityRank: "good",
    docsUrl: "https://docs.dev.runwayml.com/guides/pricing/",
    lastVerified: "2026-09-19",
  },
  {
    id: "stability-conservative-upscale",
    name: "Stable Image Conservative Upscale",
    providerId: "stability",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Credits on Stability platform; often ~$0.04–$0.20 depending on mode",
      tiers: [{ label: "unknown", amountUsd: null, unit: "unknown" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["upscaling"],
    strengths: { upscale: true },
    qualityRank: "usable",
    docsUrl: "https://platform.stability.ai/pricing",
    lastVerified: "2026-09-19",
  },

  // ─── Explicit: Seedance 2.x / Wan 3 / MiniMax H3 ─────────
  {
    id: "fal-seedance-2.5-t2v",
    name: "Seedance 2.5 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.473 per_second (fal model page)",
      tiers: [
        { label: "per_second", amountUsd: 0.473, unit: "per_second" },
      ],
      rawPricing: [
        { label: "per_second", amountUsd: 0.473, unit: "per_second" },
      ],
      normalizedUsd: 0.473,
      normalizedUnit: "per_second",
      assumptions: "fal unit price; prefer listed resolution when present",
      confidence: "verified",
    },
    limits: { maxDurationSec: 30, maxResolution: "1080p" },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "sota",
    docsUrl: "https://fal.ai/models/bytedance/seedance-2.5/text-to-video",
    lastVerified: "2026-09-19",
    endpointId: "bytedance/seedance-2.5/text-to-video",
    aliases: ["seedance-2.5", "bytedance/seedance-2.5/text-to-video"],
    notes: "ByteDance Seedance 2.5 on fal. Official BytePlus ModelArk list ~$10.70/M tokens; fal publishes ~2× token rate.",
  },
  {
    id: "byteplus-seedance-2.5",
    name: "Seedance 2.5 (BytePlus)",
    providerId: "byteplus",
    modality: "video",
    status: "preview",
    pricing: {
      raw: "ModelArk $10.70/M tokens (no video in) · $6.40/M (with video in) · ~$0.231/s @720p",
      tiers: [
        { label: "No video input", amountUsd: 10.7, unit: "per_1m_tokens" },
        { label: "With video input", amountUsd: 6.4, unit: "per_1m_tokens" },
        { label: "720p approx", amountUsd: 0.231, unit: "per_second" },
      ],
      normalizedUsd: 0.231,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    limits: { maxDurationSec: 30 },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "sota",
    docsUrl: "https://docs.byteplus.com",
    lastVerified: "2026-09-19",
    aliases: ["dreamina-seedance-2-5-260628", "seedance 2.5"],
    notes: "First-party BytePlus ModelArk rate card; API access marked coming soon on some docs — verify console.",
  },
  {
    id: "krea-seedance-2.0",
    name: "Seedance 2.0 (Krea)",
    providerId: "krea",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Seedance 2 $0.0849 (per_second) via Krea",
      tiers: [
        { label: "Seedance 2", amountUsd: 0.0849, unit: "per_second" },
      ],
      rawPricing: [
        { label: "Seedance 2", amountUsd: 0.0849, unit: "per_second" },
      ],
      normalizedUsd: 0.0849,
      normalizedUnit: "per_second",
      assumptions: "Krea API pricing page",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video", "character-reference"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true, characterReference: true },
    qualityRank: "good",
    docsUrl: "https://www.krea.ai/app/api/pricing",
    lastVerified: "2026-09-19",
    aliases: ["seedance-2.0"],
    notes: "Hosted on Krea aggregator with native audio, references, frame animation.",
  },
  {
    id: "krea-seedance-2.0-fast",
    name: "Seedance 2.0 Fast (Krea)",
    providerId: "krea",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Seedance 2 Fast $0.0677 (per_second) via Krea",
      tiers: [
        { label: "Seedance 2 Fast", amountUsd: 0.0677, unit: "per_second" },
      ],
      rawPricing: [
        { label: "Seedance 2 Fast", amountUsd: 0.0677, unit: "per_second" },
      ],
      normalizedUsd: 0.0677,
      normalizedUnit: "per_second",
      assumptions: "Krea API pricing page",
      confidence: "verified",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "usable",
    docsUrl: "https://www.krea.ai/app/api/pricing",
    lastVerified: "2026-09-19",
  },
  {
    id: "alibaba-wan-3.0",
    name: "Wan 3.0",
    providerId: "alibaba",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Model Studio intl $0.05/s 480P · $0.10/s 720P · $0.20/s 1080P",
      tiers: [
        { label: "480P", amountUsd: 0.05, unit: "per_second" },
        { label: "720P", amountUsd: 0.10, unit: "per_second" },
        { label: "1080P", amountUsd: 0.20, unit: "per_second" },
      ],
      normalizedUsd: 0.10,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    limits: { maxDurationSec: 30, maxResolution: "1080P" },
    capabilities: ["text-to-video", "image-to-video", "editing", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, edit: true, audioInVideo: true, characterReference: true },
    qualityRank: "sota",
    docsUrl: "https://help.aliyun.com/en/model-studio/wan3-0-video",
    lastVerified: "2026-09-19",
    aliases: ["wan3.0-video", "Wan3.0"],
    notes: "Alibaba Cloud Model Studio wan3.0-video. Up to 30s; multi-modal refs including documents.",
  },
  {
    id: "fal-minimax-h3",
    name: "MiniMax H3 (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.05/s 480p · $0.06/s 768p · $0.13/s 2K · $0.16/s 4K (fal)",
      tiers: [
        { label: "480p", amountUsd: 0.05, unit: "per_second" },
        { label: "768p", amountUsd: 0.06, unit: "per_second" },
        { label: "2K", amountUsd: 0.13, unit: "per_second" },
        { label: "4K", amountUsd: 0.16, unit: "per_second" },
      ],
      normalizedUsd: 0.06,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    limits: { maxDurationSec: 15, maxResolution: "4K" },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video", "character-reference"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true, characterReference: true },
    qualityRank: "sota",
    docsUrl: "https://fal.ai/models/minimax/h3/text-to-video",
    lastVerified: "2026-09-19",
    endpointId: "minimax/h3/text-to-video",
    aliases: ["minimax-h3", "MiniMax H3"],
  },
  {
    id: "fal-minimax-h3-max",
    name: "MiniMax H3 Max (fal)",
    providerId: "fal",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.05 per_second (fal model page)",
      tiers: [
        { label: "per_second", amountUsd: 0.05, unit: "per_second" },
      ],
      rawPricing: [
        { label: "per_second", amountUsd: 0.05, unit: "per_second" },
      ],
      normalizedUsd: 0.05,
      normalizedUnit: "per_second",
      assumptions: "fal unit price; prefer listed resolution when present",
      confidence: "verified",
    },
    limits: { maxDurationSec: 15, maxResolution: "1080p" },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video", "character-reference"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true, characterReference: true },
    qualityRank: "sota",
    docsUrl: "https://fal.ai/models/minimax/h3-max/text-to-video",
    lastVerified: "2026-09-19",
    endpointId: "minimax/h3-max/text-to-video",
    aliases: ["h3-max", "MiniMax Hailuo H3 Max", "Hailuo H3 Max"],
    notes: "fal Research post-trained H3 Max. Distinct from base MiniMax H3.",
  },
  {
    id: "minimax-h3-direct",
    name: "MiniMax H3 (direct)",
    providerId: "minimax",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "Verify MiniMax platform pricing; fal host rates used as estimated proxy",
      tiers: [{ label: "768p proxy (fal)", amountUsd: 0.06, unit: "per_second" }],
      normalizedUsd: 0.06,
      normalizedUnit: "per_second",
      confidence: "estimated",
    },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "sota",
    docsUrl: "https://www.minimax.io/platform",
    lastVerified: "2026-09-19",
    aliases: ["Hailuo H3", "MiniMax Hailuo H3"],
  },
  // ─── Krea (own + aggregator) ─────────────────────────────
  {
    id: "krea-2-turbo",
    name: "Krea 2 Medium Turbo",
    providerId: "krea",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Krea API ~$0.03/image",
      tiers: [
        { label: "K2 mid", amountUsd: 0.03, unit: "per_image" },
      ],
      rawPricing: [
        { label: "K2 mid", amountUsd: 0.03, unit: "per_image" },
      ],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      assumptions: "Krea API mid-quality image tier",
      confidence: "verified",
    },
    limits: { maxResolution: "1.5K native (4K via Enhancer)" },
    capabilities: ["text-to-image", "style-reference", "moodboard"],
    strengths: { textToImage: true, styleReference: true, moodboard: true },
    qualityRank: "usable",
    docsUrl: "https://www.krea.ai/app/api/pricing",
    lastVerified: "2026-09-19",
    aliases: ["K2 Medium Turbo", "Krea 2 Turbo"],
  },
  {
    id: "krea-2-medium",
    name: "Krea 2 Medium",
    providerId: "krea",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.03/image · style ref +$0.005 · moodboard +$0.01",
      tiers: [
        { label: "Base", amountUsd: 0.03, unit: "per_image" },
        { label: "+ style reference", amountUsd: 0.035, unit: "per_image" },
        { label: "+ moodboard", amountUsd: 0.04, unit: "per_image" },
      ],
      normalizedUsd: 0.03,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    limits: { maxResolution: "1.5K native (4K via Enhancer)" },
    capabilities: ["text-to-image", "style-reference", "moodboard"],
    strengths: { textToImage: true, styleReference: true, moodboard: true },
    qualityRank: "good",
    docsUrl: "https://www.krea.ai/app/api/pricing",
    lastVerified: "2026-09-19",
    aliases: ["K2 Medium"],
  },
  {
    id: "krea-2-large",
    name: "Krea 2 Large",
    providerId: "krea",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.06/image · style ref +$0.005 · moodboard +$0.01",
      tiers: [
        { label: "Base", amountUsd: 0.06, unit: "per_image" },
        { label: "+ style reference", amountUsd: 0.065, unit: "per_image" },
        { label: "+ moodboard", amountUsd: 0.07, unit: "per_image" },
      ],
      normalizedUsd: 0.06,
      normalizedUnit: "per_image",
      confidence: "verified",
    },
    limits: { maxResolution: "2K native (4K via Enhancer)" },
    capabilities: ["text-to-image", "style-reference", "moodboard"],
    strengths: { textToImage: true, styleReference: true, moodboard: true },
    qualityRank: "sota",
    docsUrl: "https://www.krea.ai/app/api/pricing",
    lastVerified: "2026-09-19",
    aliases: ["K2 Large"],
    notes: "Krea own-model flagship for photorealism. Krea also aggregates partner video/image models.",
  },
  // ─── Pruna.ai performance models ─────────────────────────
  {
    id: "pruna-p-image",
    name: "P-Image",
    providerId: "pruna",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "from ~$0.005–$0.010 / image (Pruna pricing page)",
      tiers: [
        { label: "Low", amountUsd: 0.005, unit: "per_image" },
        { label: "High", amountUsd: 0.01, unit: "per_image" },
      ],
      normalizedUsd: 0.005,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["text-to-image", "lora"],
    strengths: { textToImage: true, lora: true, training: true },
    qualityRank: "sota",
    docsUrl: "https://docs.pruna.ai/",
    lastVerified: "2026-09-19",
    notes: "Sub-second T2I with strong text rendering. LoRA training available.",
  },
  {
    id: "pruna-p-image-edit",
    name: "P-Image-Edit",
    providerId: "pruna",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "$0.045/sec (Pruna)",
      tiers: [
        { label: "listed", amountUsd: 0.045, unit: "per_second" },
      ],
      rawPricing: [
        { label: "listed", amountUsd: 0.045, unit: "per_second" },
      ],
      normalizedUsd: 0.045,
      normalizedUnit: "per_second",
      assumptions: "Pruna all-models; prefer 720p",
      confidence: "verified",
    },
    capabilities: ["editing", "image-to-image", "inpainting"],
    strengths: { edit: true, imageToImage: true, inpaint: true, lora: true, training: true },
    qualityRank: "sota",
    docsUrl: "https://docs.pruna.ai/",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-image-ideogram",
    name: "P-Image-Ideogram",
    providerId: "pruna",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "Verify Pruna portal (Ideogram-based T2I with thinking levels)",
      tiers: [],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "unknown",
    },
    capabilities: ["text-to-image"],
    strengths: { textToImage: true },
    qualityRank: "good",
    docsUrl: "https://docs.pruna.ai/",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-image-upscale",
    name: "P-Image-Upscale",
    providerId: "pruna",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "~$0.10 / 8MP upscale (Pruna pricing)",
      tiers: [{ label: "8MP", amountUsd: 0.10, unit: "per_image" }],
      normalizedUsd: 0.10,
      normalizedUnit: "per_image",
      confidence: "estimated",
    },
    capabilities: ["upscaling"],
    strengths: { upscale: true },
    qualityRank: "good",
    docsUrl: "https://docs.pruna.ai/",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-image-try-on",
    name: "P-Image-Try-On",
    providerId: "pruna",
    modality: "image",
    status: "ga",
    pricing: {
      raw: "+$0.008 / additional garment (base verify on portal)",
      tiers: [{ label: "Extra garment", amountUsd: 0.008, unit: "per_request" }],
      normalizedUsd: null,
      normalizedUnit: "unknown",
      confidence: "estimated",
    },
    capabilities: ["editing", "image-to-image", "character-reference"],
    strengths: { edit: true, imageToImage: true, characterReference: true },
    qualityRank: "good",
    docsUrl: "https://docs.pruna.ai/",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-video",
    name: "P-Video",
    providerId: "pruna",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.025/sec (Pruna)",
      tiers: [
        { label: "listed", amountUsd: 0.025, unit: "per_second" },
      ],
      rawPricing: [
        { label: "listed", amountUsd: 0.025, unit: "per_second" },
      ],
      normalizedUsd: 0.025,
      normalizedUnit: "per_second",
      assumptions: "Pruna all-models; prefer 720p",
      confidence: "verified",
    },
    capabilities: ["image-to-video", "audio-in-video"],
    strengths: { imageToVideo: true, audioInVideo: true },
    qualityRank: "good",
    docsUrl: "https://www.pruna.ai/all-models",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-video-2",
    name: "P-Video-2",
    providerId: "pruna",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.025/sec · 720p (pruna.ai/all-models)",
      tiers: [{ label: "720p", amountUsd: 0.025, unit: "per_second" }],
      normalizedUsd: 0.025,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    limits: { maxDurationSec: 20, maxResolution: "1080p" },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true, characterReference: true },
    qualityRank: "sota",
    docsUrl: "https://www.pruna.ai/all-models",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-video-2-pro",
    name: "P-Video-2-Pro",
    providerId: "pruna",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.02/sec · 480p (listed); based on MiniMax H3",
      tiers: [{ label: "480p", amountUsd: 0.02, unit: "per_second" }],
      normalizedUsd: 0.02,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    limits: { maxDurationSec: 15, maxResolution: "768p" },
    capabilities: ["text-to-video", "image-to-video", "audio-in-video"],
    strengths: { textToVideo: true, imageToVideo: true, audioInVideo: true },
    qualityRank: "sota",
    docsUrl: "https://docs.pruna.ai/",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-video-avatar",
    name: "P-Video-Avatar",
    providerId: "pruna",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.025/sec (Pruna)",
      tiers: [
        { label: "listed", amountUsd: 0.025, unit: "per_second" },
      ],
      rawPricing: [
        { label: "listed", amountUsd: 0.025, unit: "per_second" },
      ],
      normalizedUsd: 0.025,
      normalizedUnit: "per_second",
      assumptions: "Pruna all-models; prefer 720p",
      confidence: "verified",
    },
    capabilities: ["image-to-video", "audio-in-video", "character-reference"],
    strengths: { imageToVideo: true, audioInVideo: true, characterReference: true },
    qualityRank: "good",
    docsUrl: "https://www.pruna.ai/all-models",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-video-edit",
    name: "P-Video-Edit",
    providerId: "pruna",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.045/sec · 720p",
      tiers: [{ label: "720p", amountUsd: 0.045, unit: "per_second" }],
      normalizedUsd: 0.045,
      normalizedUnit: "per_second",
      confidence: "verified",
    },
    limits: { maxDurationSec: 15 },
    capabilities: ["editing"],
    strengths: { edit: true },
    qualityRank: "good",
    docsUrl: "https://www.pruna.ai/all-models",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-video-replace",
    name: "P-Video-Replace",
    providerId: "pruna",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.03/sec (Pruna)",
      tiers: [
        { label: "listed", amountUsd: 0.03, unit: "per_second" },
      ],
      rawPricing: [
        { label: "listed", amountUsd: 0.03, unit: "per_second" },
      ],
      normalizedUsd: 0.03,
      normalizedUnit: "per_second",
      assumptions: "Pruna all-models; prefer 720p",
      confidence: "verified",
    },
    capabilities: ["image-to-video", "character-reference", "editing"],
    strengths: { imageToVideo: true, characterReference: true, edit: true },
    qualityRank: "good",
    docsUrl: "https://www.pruna.ai/all-models",
    lastVerified: "2026-09-19",
  },
  {
    id: "pruna-p-video-animate",
    name: "P-Video-Animate",
    providerId: "pruna",
    modality: "video",
    status: "ga",
    pricing: {
      raw: "$0.03/sec (Pruna)",
      tiers: [
        { label: "listed", amountUsd: 0.03, unit: "per_second" },
      ],
      rawPricing: [
        { label: "listed", amountUsd: 0.03, unit: "per_second" },
      ],
      normalizedUsd: 0.03,
      normalizedUnit: "per_second",
      assumptions: "Pruna all-models; prefer 720p",
      confidence: "verified",
    },
    capabilities: ["image-to-video"],
    strengths: { imageToVideo: true },
    qualityRank: "usable",
    docsUrl: "https://www.pruna.ai/all-models",
    lastVerified: "2026-09-19",
  },

];
